import { FISun, FoldProvider, setFoldIcons, Spinner, View } from '@fold-dev/core'
import { useEffect } from 'react'
import { BsMarkdown } from 'react-icons/bs'
import {
    TbAirBalloon,
    TbAnchor,
    TbArchive,
    TbAt,
    TbBell,
    TbBook,
    TbBookmark,
    TbBrightness2,
    TbBroadcast,
    TbBulb,
    TbCalendar,
    TbCamera,
    TbCarFan,
    TbCat,
    TbChartBar,
    TbCheck,
    TbCheckbox,
    TbChecklist,
    TbClock,
    TbCloud,
    TbCube,
    TbDog,
    TbDots,
    TbExclamationCircle,
    TbFlag,
    TbFlag2,
    TbFlag3,
    TbFlame,
    TbGlobe,
    TbHash,
    TbHeart,
    TbInbox,
    TbKey,
    TbLayoutGrid,
    TbLink,
    TbList,
    TbListNumbers,
    TbMap,
    TbMapPin,
    TbMessage,
    TbMessageCircle,
    TbMoodSmile,
    TbMusic,
    TbPaperclip,
    TbPencil,
    TbPhone,
    TbPhoto,
    TbRocket,
    TbSearch,
    TbSettings,
    TbShield,
    TbSpeakerphone,
    TbSquareRoundedPlus,
    TbStar,
    TbSun,
    TbTag,
    TbTrophy,
    TbUmbrella,
    TbUserSquareRounded
} from 'react-icons/tb'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Task, WorkspacesView } from './components'
import { NODE_ENV } from './constants'
import { shouldRedirecToAuth } from './helpers'
import './helpers/extensions'
import { useAuth } from './hooks/auth.hook'
import { AppLayout } from './layouts/app.layout'
import { AuthLayout } from './layouts/auth.layout'
import { CalendarLayout } from './layouts/calendar.layout'
import { ConfirmEmailLayout } from './layouts/confirm-email'
import { InboxLayout } from './layouts/inbox.layout'
import ProjectJoinLayout from './layouts/project-join.layout'
import ProjectPublicLayout from './layouts/project-public.layout'
import { ProjectLayout } from './layouts/project.layout'
import RoadmapLayout from './layouts/roadmap.layout'
import { SidebarModalLayout } from './layouts/sidebar.layout'
import { ThreadsLayout } from './layouts/threads.layout'
import { TodayLayout } from './layouts/today.layout'
import { UnassignedLayout } from './layouts/unassigned.layout'
import { UpcomingLayout } from './layouts/upcoming.layout'
import { WorkspaceJoinLayout } from './layouts/workspace-join.layout'
import { WorkspaceLayout } from './layouts/workspace.layout'

setFoldIcons({
    // project (and system) icons:
    'bell': TbBell,
    'pen': TbPencil,
    'light': TbBulb,
    'flag': TbFlag3,
    'smile': TbMoodSmile,
    'shield': TbShield,
    'smartphone': TbPhone,
    'hash': TbHash,
    'map': TbMap,
    'cloud': TbCloud,
    'nice-check': TbCheck,
    'radio': TbBroadcast,
    'box': TbCube,
    'megaphone': TbSpeakerphone,
    'fire': TbFlame,
    'heart': TbHeart,
    'music': TbMusic,
    'photo': TbPhoto,
    'star': TbStar,
    'key': TbKey,
    'book': TbBook,
    'camera': TbCamera,
    'globe': TbGlobe,
    'clock': TbClock,
    'anchor': TbAnchor,
    'tag': TbTag,
    'umbrella': TbUmbrella,
    'trophy': TbTrophy,
    'cat': TbCat,
    'dog': TbDog,
    'rocket': TbRocket,
    'at': TbAt,
    'pin': TbMapPin,
    'carfan': TbCarFan,
    'airballoon': TbAirBalloon,
    'chart': TbChartBar,
    // system only icons:
    'link': TbLink,
    'list-numbered': TbListNumbers,
    'list-bulleted': TbList,
    'search': TbSearch,
    'markdown': BsMarkdown,
    'more': TbDots,
    'inbox': TbInbox,
    'today': TbSun,
    'tasks': TbCheckbox,
    'calendar': TbCalendar,
    'settings': TbSettings,
    'team': TbUserSquareRounded,
    'importance': TbFlag,
    'unassigned': TbBookmark,
    'new': TbSquareRoundedPlus,
    'bookmark': TbBookmark,
    'labels': TbTag,
    'projects': TbLayoutGrid,
    'archive': TbArchive,
    'upcoming': TbChecklist,
    'messages': TbMessage,
    'main:inbox': TbInbox,
    'main:unassigned': TbBookmark,
    'main:today': FISun,
    'main:upcoming': TbChecklist,
    'main:calendar': TbCalendar,
    'main:threads': TbMessageCircle,
    'main:team': TbUserSquareRounded,
    'paperclip': TbPaperclip,
    // task types:
    'task-type:milestone': TbFlag2,
    'task-type:blocker': TbExclamationCircle,
})

export const Busy = () => {
    return (
        <View
            row
            bg="var(--busy)"
            width="100%"
            height="100%"
            position="sticky"
            zIndex={100}
            style={{ top: 0, left: 0 }}>
            <Spinner />
        </View>
    )
}

export const FRONTEER_HEARTBEATS = 'FRONTEER_HEARTBEATS'
window[FRONTEER_HEARTBEATS] = {}

const App = (props: any) => {
    const { currentAuthenticatedUser, getToken } = useAuth()

    useEffect(() => {
        const { pathname } = window.location
        const isElectron = pathname.includes('index.html')
        const isRoot = pathname == '/'

        if (isElectron || isRoot) {
            currentAuthenticatedUser()
                .then((res) => {
                    window.location.href = '/app'
                })
                .catch((err) => {
                    if (shouldRedirecToAuth()) window.location.href = window.location.origin + '/auth'
                })
        }

        if (NODE_ENV == 'production') {
            console.log(
                `
░█▀▀░█▀▄░█▀█░█▀█░▀█▀░█▀▀░█▀▀░█▀▄
░█▀▀░█▀▄░█░█░█░█░░█░░█▀▀░█▀▀░█▀▄
░▀░░░▀░▀░▀▀▀░▀░▀░░▀░░▀▀▀░▀▀▀░▀░▀
                `
            )
            console.log(
                '%cWelcome to Fronteer!\n%cNeed help? Email us at: %csupport@fronteer.app',
                'font-size: 16px; font-weight: bold;',
                'font-size: 14px;',
                'color: rgb(110, 61, 255); text-decoration: underline; font-size: 14px;'
            )
        }
    }, [])

    return (
        <BrowserRouter
            future={{
                v7_relativeSplatPath: false,
                v7_startTransition: false,
            }}>
            <FoldProvider>
              {/*   <View bg="black" p={5}>
                    <View row>
                        {[50,100,200,300,400,500,600,700,800,900,1000].map((t) => <View bgToken={`base-${t}`} width={50} height={150} />)}
                    </View>
                    <View row>
                        {[50,100,200,300,400,500,600,700,800,900,1000].map((t) => <View bgToken={`accent-${t}`} width={50} height={150} />)}
                    </View>
                </View> */}
                <Routes>
                    <Route
                        path="/"
                        element={<Busy />}
                    />

                    <Route
                        path="/auth"
                        element={<AuthLayout />}
                    />

                    <Route
                        path="/confirm-email"
                        element={<ConfirmEmailLayout />}
                    />

                    <Route
                        path="/workspace-join"
                        element={<WorkspaceJoinLayout />}
                    />

                    <Route
                        path="/roadmap/:shortcode"
                        element={<RoadmapLayout />}
                    />

                    <Route
                        path="/p/:shortcode"
                        element={<ProjectPublicLayout />}
                    />

                    <Route
                        path="/c/:shortcode"
                        element={<ProjectJoinLayout />}
                    />

                    <Route
                        path="/app"
                        element={<WorkspacesView />}
                    />

                    <Route
                        path="/app"
                        element={<AppLayout />}>
                        <Route
                            path="workspace/:workspaceId"
                            element={<WorkspaceLayout />}>
                            <Route
                                path="threads"
                                element={<ThreadsLayout />}
                            />

                            {/* Inbox */}
                            <Route
                                path="inbox"
                                element={<InboxLayout />}>
                                <Route
                                    path="task/:taskId"
                                    element={
                                        <Task
                                            direction="vertical"
                                            location="inbox"
                                            urlPattern="/app/workspace/:workspaceId/inbox/task/:taskId"
                                        />
                                    }
                                />
                            </Route>

                            {/* Unassigned */}
                            <Route
                                path="unassigned"
                                element={<UnassignedLayout />}>
                                <Route
                                    path="task"
                                    element={<SidebarModalLayout />}>
                                    <Route
                                        path=":taskId"
                                        element={
                                            <Task
                                                location="unassigned"
                                                urlPattern="/app/workspace/:workspaceId/unassigned/task/:taskId"
                                            />
                                        }
                                    />
                                </Route>
                            </Route>

                            {/* Calendar */}
                            <Route
                                path="calendar"
                                element={<CalendarLayout />}>
                                <Route
                                    path="task"
                                    element={<SidebarModalLayout />}>
                                    <Route
                                        path=":taskId"
                                        element={
                                            <Task
                                                location="calendar"
                                                urlPattern="/app/workspace/:workspaceId/calendar/task/:taskId"
                                            />
                                        }
                                    />
                                </Route>
                            </Route>

                            {/* Upcoming */}
                            <Route
                                path="upcoming"
                                element={<UpcomingLayout />}>
                                <Route
                                    path="task"
                                    element={<SidebarModalLayout />}>
                                    <Route
                                        path=":taskId"
                                        element={
                                            <Task
                                                location="upcoming"
                                                urlPattern="/app/workspace/:workspaceId/upcoming/task/:taskId"
                                            />
                                        }
                                    />
                                </Route>
                            </Route>

                            {/* Today */}
                            <Route
                                path="today"
                                element={<TodayLayout />}>
                                <Route
                                    path="task"
                                    element={<SidebarModalLayout />}>
                                    <Route
                                        path=":taskId"
                                        element={
                                            <Task
                                                location="today"
                                                urlPattern="/app/workspace/:workspaceId/today/task/:taskId"
                                            />
                                        }
                                    />
                                </Route>
                            </Route>

                            {/* Project */}
                            <Route
                                path="c/:projectId/:view"
                                element={<ProjectLayout />}>
                                <Route
                                    path="task"
                                    element={<SidebarModalLayout />}>
                                    <Route
                                        path=":taskId"
                                        element={
                                            <Task
                                                location="project"
                                                urlPattern="/app/workspace/:workspaceId/c/:projectId/:view/task/:taskId"
                                            />
                                        }
                                    />
                                </Route>
                            </Route>
                        </Route>
                    </Route>
                </Routes>
            </FoldProvider>
        </BrowserRouter>
    )
}

export default App
